import { Box, Card, Divider, Stack, Typography } from "@mui/material";
import {
  useAutomationIntegrationDeploymentApiV1GetActiveJobsEndpointQuery,
  useAutomationIntegrationDeploymentApiV1GetConsumerOverviewQuery,
  useAutomationIntegrationDeploymentApiV1GetPendingJobsEndpointQuery,
} from "@providers/hop-ord-server/api";
import { useEffect, useRef, useState } from "react";
import { ActiveJobsTable } from "./ActiveJobsTable";
import { ConsumerTable } from "./ConsumerTable";
import { PendingJobsTable } from "./PendingJobsTable";
import StatusRow from "./StatusRow";
import RefreshInfoButton from "./RefreshInfoButton";

export const JobsOverviewCard = ({ tenantId }: { tenantId: number }) => {
  const [lastConsumerRefresh, setLastConsumerRefresh] = useState<Date>(
    new Date(),
  );
  const [lastActiveJobsRefresh, setLastActiveJobsRefresh] = useState<Date>(
    new Date(),
  );
  const [lastPendingJobsRefresh, setLastPendingJobsRefresh] = useState<Date>(
    new Date(),
  );
  const consumerRefreshTriggered = useRef(false);
  const activeJobsRefreshTriggered = useRef(false);
  const pendingJobsRefreshTriggered = useRef(false);

  const {
    data: consumerInfoData,
    isLoading: consumerInfoLoading,
    error: consumerInfoError,
    refetch: refetchConsumerInfo,
    isFetching: consumerInfoFetching,
  } = useAutomationIntegrationDeploymentApiV1GetConsumerOverviewQuery({
    tenantIds: [tenantId],
  });

  const {
    data: activeJobsData,
    isLoading: activeJobsLoading,
    error: activeJobsError,
    refetch: refetchActiveJobs,
    isFetching: activeJobsFetching,
  } = useAutomationIntegrationDeploymentApiV1GetActiveJobsEndpointQuery({
    tenantId,
  });

  const {
    data: pendingJobsData,
    isLoading: pendingJobsLoading,
    error: pendingJobsError,
    refetch: refetchPendingJobs,
    isFetching: pendingJobsFetching,
  } = useAutomationIntegrationDeploymentApiV1GetPendingJobsEndpointQuery({
    tenantId,
  });

  // Derived state
  const consumerInfo = consumerInfoData || [];
  const activeJobs = activeJobsData || [];
  const pendingJobs = pendingJobsData || [];

  const handleConsumerRefresh = () => {
    consumerRefreshTriggered.current = true;
    refetchConsumerInfo();
  };

  const handleActiveJobsRefresh = () => {
    activeJobsRefreshTriggered.current = true;
    refetchActiveJobs();
  };

  const handlePendingJobsRefresh = () => {
    pendingJobsRefreshTriggered.current = true;
    refetchPendingJobs();
  };

  // Update timestamps when responses are received after manual refresh
  useEffect(() => {
    if (!consumerInfoFetching && consumerRefreshTriggered.current) {
      setLastConsumerRefresh(new Date());
      consumerRefreshTriggered.current = false;
    }
  }, [consumerInfoFetching]);

  useEffect(() => {
    if (!activeJobsFetching && activeJobsRefreshTriggered.current) {
      setLastActiveJobsRefresh(new Date());
      activeJobsRefreshTriggered.current = false;
    }
  }, [activeJobsFetching]);

  useEffect(() => {
    if (!pendingJobsFetching && pendingJobsRefreshTriggered.current) {
      setLastPendingJobsRefresh(new Date());
      pendingJobsRefreshTriggered.current = false;
    }
  }, [pendingJobsFetching]);

  // Group consumers by group name for better display
  const tenantAutopintOverview = consumerInfo.at(0);
  const totalConsumers =
    (tenantAutopintOverview?.consumers.planGenConsumers.length || 0) +
    (tenantAutopintOverview?.consumers.otherConsumers.length || 0);

  return (
    <Card variant="outlined">
      <Stack
        sx={{
          padding: 2,
          gap: 2,
        }}
      >
        {/* Generation Jobs Status */}
        <Typography variant="h5">Job Status</Typography>

        <Stack
          direction="row"
          sx={{
            gap: 2,
          }}
        >
          {/* Consumer Information by Group */}
          <Box
            sx={{
              flex: 1,
            }}
          >
            {consumerInfoLoading ? (
              <StatusRow message="Loading consumer info..." type="loading" />
            ) : consumerInfoError ? (
              <StatusRow message="Error loading consumer info" type="error" />
            ) : (
              <>
                <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                  <Typography variant="h6">
                    Redis Consumers ({totalConsumers} total)
                  </Typography>
                  <RefreshInfoButton
                    onClick={handleConsumerRefresh}
                    lastRefreshedAt={lastConsumerRefresh}
                    isFetching={consumerInfoFetching}
                  />
                </Stack>
                <ConsumerTable
                  backgroundConsumers={
                    tenantAutopintOverview?.consumers.otherConsumers || []
                  }
                  planGenConsumers={
                    tenantAutopintOverview?.consumers.planGenConsumers || []
                  }
                />
              </>
            )}
          </Box>
          <Stack
            direction="column"
            sx={{ gap: 2, flex: 1, justifyContent: "space-between" }}
            divider={<Divider />}
          >
            {/* Active Jobs Details */}
            <Box>
              {activeJobsLoading ? (
                <StatusRow message="Loading active jobs..." type="loading" />
              ) : activeJobsError ? (
                <StatusRow message="Error loading active jobs" type="error" />
              ) : (
                <>
                  <Stack
                    direction="row"
                    sx={{ justifyContent: "space-between" }}
                  >
                    <Typography variant="h6">Active Jobs</Typography>
                    <RefreshInfoButton
                      onClick={handleActiveJobsRefresh}
                      lastRefreshedAt={lastActiveJobsRefresh}
                      isFetching={activeJobsFetching}
                    />
                  </Stack>
                  <ActiveJobsTable activeJobs={activeJobs} />
                </>
              )}
            </Box>
            {/* Pending Jobs Details */}
            <Box>
              {pendingJobsLoading ? (
                <StatusRow message="Loading pending jobs..." type="loading" />
              ) : pendingJobsError ? (
                <StatusRow message="Error loading pending jobs" type="error" />
              ) : (
                <>
                  <Stack
                    direction="row"
                    sx={{ justifyContent: "space-between" }}
                  >
                    <Typography variant="h6">Pending Jobs</Typography>
                    <RefreshInfoButton
                      onClick={handlePendingJobsRefresh}
                      lastRefreshedAt={lastPendingJobsRefresh}
                      isFetching={pendingJobsFetching}
                    />
                  </Stack>
                  <PendingJobsTable pendingJobs={pendingJobs} />
                </>
              )}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
